import React, { useEffect, useRef } from "react";
import * as fabric from "fabric";
import { motion } from "framer-motion";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const Whiteboard = ({ highlightedSentence, content, imageUrls, videoUrl }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null); // Reference for the Fabric.js canvas object
  //az storage blob upload-batch --account-name platostaticstorage --destination '$web' --source ./build --overwrite

  const displayText = content ?? highlightedSentence;

  // Parse parts once — used by both paths
  const parts = displayText
    ? displayText.split("|").map(p => p.trim()).filter(Boolean)
    : [];

  // If any part has a formula: prefix, use the HTML/KaTeX render path
  const hasMixedContent = parts.some(p => p.toLowerCase().startsWith("formula:"));

  useEffect(() => {
    if (hasMixedContent) return; // HTML path handles mixed/formula content

    // Initialize Fabric.js canvas
    fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
      backgroundColor: "#f4f6f9",
      rx: 16,
      ry: 16
    });
    const CANVAS_W = 520;
    fabricCanvasRef.current.setWidth(CANVAS_W);
    fabricCanvasRef.current.setHeight(400); // initial height, grows below if needed

    if(displayText){
      const PAD = 32;

      // Detect optional title prefix
      let titleText = null;
      let bulletParts = parts;
      if (parts.length > 0 && parts[0].toLowerCase().startsWith("title:")) {
        titleText = parts[0].substring(6).trim();
        bulletParts = parts.slice(1);
      }

      let currentTop = PAD;

      // Render bold title if present
      if (titleText) {
        const titlebox = new fabric.Textbox(titleText, {
          left: PAD,
          top: currentTop,
          width: CANVAS_W - PAD * 2,
          fontSize: 24,
          fill: "#1a1a2e",
          fontFamily: "Inter, system-ui, sans-serif",
          fontWeight: "700",
          textAlign: "left",
          lineHeight: 1.4,
        });
        fabricCanvasRef.current.add(titlebox);
        fabricCanvasRef.current.renderAll();
        currentTop = Math.ceil(titlebox.top + titlebox.height) + 14;
      }

      // Render bullet points below title (or plain text if single part)
      if (bulletParts.length > 0) {
        const formattedText = bulletParts.length > 1
          ? bulletParts.map(p => `• ${p}`).join("\n")
          : bulletParts[0];
        const textbox = new fabric.Textbox(formattedText, {
          left: PAD,
          top: currentTop,
          width: CANVAS_W - PAD * 2,
          fontSize: 18,
          fill: "#1a1a2e",
          fontFamily: "Inter, system-ui, sans-serif",
          fontWeight: "500",
          textAlign: "left",
          lineHeight: 1.65,
        });
        fabricCanvasRef.current.add(textbox);
        fabricCanvasRef.current.renderAll();

        // Expand canvas height to fit all text
        const neededHeight = Math.ceil(textbox.top + textbox.height + PAD);
        if (neededHeight > 400) {
          fabricCanvasRef.current.setHeight(neededHeight);
          fabricCanvasRef.current.renderAll();
        }
        fabricCanvasRef.current.textObject = textbox;
      }
    }

    if (imageUrls) {
      // Add image object
      fabric.FabricImage.fromURL(imageUrls[0], (img) => {
        img.set({
          left: 100,
          top: 150,
          scaleX: 0.5, // Scale the image
          scaleY: 0.3,
        });

        fabricCanvasRef.current.add(img);
      },  { crossOrigin: "anonymous" });
      // Add image object
      fabric.FabricImage.fromURL(imageUrls[1], (img) => {
        img.set({
          left: 100,
          top: 150,
          scaleX: 0.5, // Scale the image
          scaleY: 0.3,
        });

        fabricCanvasRef.current.add(img);
      }, { crossOrigin: "anonymous" });
    }


    fabricCanvasRef.current.renderAll();

    // Cleanup function to dispose of Fabric.js canvas
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [displayText, hasMixedContent]);

  const containerStyle = {
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
    background: "rgba(255,255,255,0.04)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "inline-block",
    width: "520px",
    flexShrink: 0,
    verticalAlign: "top",
    height: "400px"
  };

  // Mixed content (has formula: items) → HTML/KaTeX render path
  if (hasMixedContent) {
    return (
      <div style={{ ...containerStyle, lineHeight: "normal", background: "#f4f6f9", overflowY: "auto" }}>
        <motion.div
          key={displayText}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: "12px" }}
        >
          {parts.map((part, idx) => {
            if (part.toLowerCase().startsWith("title:")) {
              return (
                <div key={idx} style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1a1a2e",
                  fontFamily: "Inter, system-ui, sans-serif",
                  lineHeight: 1.3,
                  marginBottom: "4px",
                }}>
                  {part.substring(6).trim()}
                </div>
              );
            }
            if (part.toLowerCase().startsWith("formula:")) {
              return (
                <div key={idx} style={{ display: "flex", justifyContent: "center" }}>
                  <BlockMath math={part.substring(8).trim()} />
                </div>
              );
            }
            return (
              <div key={idx} style={{
                fontSize: "17px",
                color: "#1a1a2e",
                fontFamily: "Inter, system-ui, sans-serif",
                fontWeight: "500",
                lineHeight: 1.6,
              }}>
                • {part}
              </div>
            );
          })}
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ ...containerStyle, lineHeight: 0 }}>
      <motion.div
        key={displayText}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <canvas ref={canvasRef} style={{ display: "block", borderRadius: "16px" }} />
      </motion.div>
    </div>
  );
};

export default Whiteboard;
